import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, Loader2, RotateCcw, ArrowLeft, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n, c) => `${c} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MAX_POLLS = 20;

const Shell = ({ children }) => (
  <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 sm:p-6" data-testid="invoice-page">
    {children}
  </div>
);

const PoweredBy = () => (
  <a href="https://bookingflows.com" className="mt-6 text-[11px] text-gray-300 hover:text-gray-400 transition-colors">
    Powered by <span className="font-semibold text-gray-400">BookingFlows</span>
  </a>
);

/** Public branded invoice / receipt page — customers land here after paying.
 *  States: loading · not found · awaiting payment · confirming · slow-confirm ·
 *  cancelled/failed (retry) · paid. Flat: white page, light-gray card, no borders. */
export default function InvoicePage() {
  const { invoiceNumber } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(false);
  const [slow, setSlow] = useState(false);
  const polls = useRef(0);

  const isStaff = !!localStorage.getItem('jwtToken');
  const justPaid = params.get('paid') === '1';
  const wasCancelled = params.get('cancelled') === '1';
  const wasFailed = params.get('failed') === '1';

  const verify = useCallback(async () => {
    try {
      const r = await axios.post(`${BACKEND_URL}/api/v2/public/invoices/${invoiceNumber}/verify`);
      setInv(r.data);
      return r.data;
    } catch {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/v2/public/invoices/${invoiceNumber}`);
        setInv(r.data);
        return r.data;
      } catch { setError(true); }
    }
    return null;
  }, [invoiceNumber]);

  useEffect(() => { verify(); }, [verify]);

  useEffect(() => {
    if (!inv || inv.status !== 'pending' || !justPaid) return;
    if (polls.current >= MAX_POLLS) { setSlow(true); return; }
    const t = setTimeout(async () => { polls.current += 1; await verify(); }, 6000);
    return () => clearTimeout(t);
  }, [inv, verify, justPaid]);

  /* ── loading — BookingFlows blue loader on white ── */
  if (!inv && !error) {
    return (
      <Shell>
        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" data-testid="invoice-loading" />
      </Shell>
    );
  }

  /* ── not found ── */
  if (error) {
    return (
      <Shell>
        <div className="w-full max-w-md bg-[#f5f6f8] rounded-[28px] px-8 py-12 text-center" data-testid="invoice-not-found">
          <p className="text-[15px] font-semibold text-gray-900">Invoice not found</p>
          <p className="text-[13px] text-gray-400 mt-1.5">This link may have expired or the invoice number is incorrect.</p>
          {isStaff && (
            <button
              data-testid="invoice-back-dashboard-btn"
              onClick={() => navigate('/admin')}
              className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-medium text-gray-500 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
            </button>
          )}
        </div>
        <PoweredBy />
      </Shell>
    );
  }

  const brand = inv.org?.primaryColor || '#2563eb';
  const paid = inv.status === 'paid';
  const failed = !paid && (wasFailed || inv.status === 'failed');
  const cancelled = !paid && !failed && wasCancelled;
  const confirming = !paid && !failed && !cancelled && justPaid && !slow;

  return (
    <Shell>
      <div className="w-full max-w-md bg-[#f5f6f8] rounded-[28px] px-6 sm:px-8 pt-9 pb-8">
        {/* Header — org identity */}
        <div className="text-center">
          {inv.org?.logoUrl ? (
            <img src={inv.org.logoUrl} alt={inv.org?.name} className="h-12 w-12 rounded-2xl object-cover mx-auto" />
          ) : (
            <div className="h-12 w-12 rounded-2xl mx-auto flex items-center justify-center text-white text-lg font-bold" style={{ backgroundColor: brand }}>
              {(inv.org?.name || 'B')[0]}
            </div>
          )}
          <p className="text-[15px] font-semibold text-gray-900 mt-3">{inv.org?.name}</p>
          <p className="text-[12px] text-gray-400 mt-0.5" data-testid="invoice-number">
            {paid ? 'Receipt' : 'Invoice'} {inv.invoiceNumber}
          </p>
        </div>

        {/* Status + total */}
        <div className="text-center mt-7">
          {paid && (
            <span data-testid="invoice-paid-stamp" className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-emerald-100/70 text-emerald-700 text-[13px] font-semibold tracking-wide">
              <Check className="w-4 h-4" /> PAID
            </span>
          )}
          {(failed || cancelled) && (
            <span data-testid="invoice-failed" className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-red-100/60 text-red-600 text-[13px] font-medium">
              <X className="w-4 h-4" /> {failed ? 'Payment failed' : 'Payment not completed'}
            </span>
          )}
          {confirming && (
            <span data-testid="invoice-pending" className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-blue-100/60 text-blue-700 text-[13px] font-medium">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Confirming payment…
            </span>
          )}
          {!paid && !failed && !cancelled && !confirming && (
            <span data-testid="invoice-pending" className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-amber-100/60 text-amber-700 text-[13px] font-medium">
              Awaiting payment
            </span>
          )}

          <p className="text-4xl font-semibold tracking-tight text-gray-900 tabular-nums mt-4" data-testid="invoice-total">
            {fmt(inv.total, inv.currency)}
          </p>
          {inv.description && <p className="text-[13px] text-gray-500 mt-2">{inv.description}</p>}
          {slow && !paid && (
            <p className="text-[12px] text-gray-400 mt-3 max-w-[300px] mx-auto" data-testid="invoice-slow-note">
              This is taking longer than usual. If you completed the payment, your receipt will update shortly — or refresh this page.
            </p>
          )}
        </div>

        {/* Details — white inner panel, no borders */}
        <div className="mt-7 bg-white rounded-2xl px-5 py-4 space-y-2">
          {[
            ['Amount', fmt(inv.amount, inv.currency)],
            inv.vatAmount ? [`${inv.taxLabel} (${Number(inv.vatRate)}%)`, fmt(inv.vatAmount, inv.currency)] : null,
            ['Total', fmt(inv.total, inv.currency)],
            inv.customerName ? ['Billed to', inv.customerName] : null,
            inv.bookingCode ? ['Booking', inv.bookingCode] : null,
            paid && inv.paidAt ? ['Paid', new Date(inv.paidAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })] : null,
          ].filter(Boolean).map(([k, v]) => (
            <div key={k} className="flex justify-between text-[13px]">
              <span className="text-gray-400">{k}</span>
              <span className={`tabular-nums ${k === 'Total' ? 'text-gray-900 font-semibold' : 'text-gray-700'}`}>{v}</span>
            </div>
          ))}
        </div>

        {paid && inv.receiptSentTo && (
          <p className="text-[12px] text-gray-400 mt-4 text-center" data-testid="invoice-receipt-sent">Receipt sent to {inv.receiptSentTo}</p>
        )}

        {/* Actions */}
        <div className="mt-6 space-y-3">
          {!paid && inv.checkoutUrl && (failed || cancelled) && (
            <button
              data-testid="invoice-retry-btn"
              onClick={() => window.location.assign(inv.checkoutUrl)}
              className="w-full py-3.5 rounded-2xl text-white text-[15px] font-semibold transition-opacity hover:opacity-90 flex items-center justify-center gap-2"
              style={{ backgroundColor: brand }}
            >
              <RotateCcw className="w-4 h-4" /> Retry payment
            </button>
          )}
          {!paid && inv.checkoutUrl && !failed && !cancelled && !confirming && (
            <button
              data-testid="invoice-pay-btn"
              onClick={() => window.location.assign(inv.checkoutUrl)}
              className="w-full py-3.5 rounded-2xl text-white text-[15px] font-semibold transition-opacity hover:opacity-90"
              style={{ backgroundColor: brand }}
            >
              Pay {fmt(inv.total, inv.currency)}
            </button>
          )}
          {isStaff && (
            <button
              data-testid="invoice-back-dashboard-btn"
              onClick={() => navigate('/admin')}
              className={paid
                ? 'w-full py-3.5 rounded-2xl bg-white text-gray-700 text-[15px] font-semibold hover:text-gray-900 transition-colors flex items-center justify-center gap-2'
                : 'w-full flex items-center justify-center gap-1.5 text-[13px] font-medium text-gray-400 hover:text-gray-700 transition-colors py-1'}
            >
              <ArrowLeft className="w-4 h-4" /> Back to dashboard
            </button>
          )}
        </div>
      </div>
      <PoweredBy />
    </Shell>
  );
}
